import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { completeEnquiry } from "../utils/enquiryConfirmation";
import "./FloatingSupportChat.css";
import { trackFunnelExit, trackFunnelEvent } from "../utils/funnelTracking";

const FLOW_OPTIONS = [
  { id: "enquiry", label: "New journey", hint: "Taxi, minibus or coach help" },
  { id: "existing_booking", label: "Existing booking", hint: "Change, question or update" },
  { id: "payment_issue", label: "Payment issue", hint: "Payment failed or unclear" },
  { id: "general", label: "General support", hint: "Anything else" },
];

const SUPPORT_CALL_HREF = "tel:+441628665040";
const SUPPORT_WHATSAPP_HREF = "https://api.whatsapp.com/send?phone=447593308428";

const FLOW_STEPS = {
  enquiry: [
    { key: "pickup_location", label: "Pickup location", type: "text" },
    { key: "dropoff_location", label: "Drop-off location", type: "text" },
    { key: "passengers", label: "How many passengers?", type: "number" },
    { key: "bags", label: "How many bags?", type: "number" },
    { key: "pickup_datetime", label: "Pickup date and time", type: "datetime-local" },
    { key: "name", label: "Your name", type: "text" },
    { key: "email", label: "Email", type: "email" },
    { key: "phone", label: "Phone", type: "tel" },
    { key: "message", label: "Anything else?", type: "textarea", optional: true },
  ],
  existing_booking: [
    { key: "booking_ref", label: "Booking reference", type: "text" },
    { key: "email", label: "Email used for booking", type: "email" },
    { key: "phone", label: "Phone", type: "tel" },
    { key: "message", label: "What do you need help with?", type: "textarea" },
  ],
  payment_issue: [
    { key: "booking_ref", label: "Booking reference", type: "text" },
    { key: "email", label: "Email", type: "email" },
    { key: "phone", label: "Phone", type: "tel" },
    { key: "message", label: "What happened?", type: "textarea" },
  ],
  general: [
    { key: "name", label: "Your name", type: "text", optional: true },
    { key: "email", label: "Email", type: "email" },
    { key: "phone", label: "Phone", type: "tel", optional: true },
    { key: "message", label: "How can we help?", type: "textarea" },
  ],
};

const AJP_MINIBUS_ENQUIRY_STEPS = [
  { key: "pickup_location", label: "Pickup location", type: "text" },
  { key: "dropoff_location", label: "Drop-off location", type: "text" },
  { key: "flight_number", label: "Flight number", type: "text", optional: true },
  { key: "flight_time", label: "Flight date and time", type: "datetime-local", optional: true },
  { key: "pickup_datetime", label: "Pickup date and time", type: "datetime-local" },
  { key: "passengers", label: "How many passengers?", type: "number" },
  { key: "bags", label: "How many bags?", type: "number" },
  { key: "name", label: "Your name", type: "text" },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "message", label: "Anything else?", type: "textarea", optional: true },
];

function detectBrand(explicitBrand) {
  if (explicitBrand === "coachgrab") return "cheapcoachgrab";
  if (explicitBrand) return explicitBrand;
  const siteMode = process.env.REACT_APP_SITE_MODE || "";
  if (siteMode) return siteMode;
  if (typeof window === "undefined") return "ccg";
  const host = window.location.hostname;
  if (host.includes("minibusgrab")) return "minibusgrab";
  if (host.includes("cheapcoachgrab")) return "cheapcoachgrab";
  if (host.includes("airportjourneyplanner")) return "planner";
  return "ccg";
}

function brandLabel(brand) {
  if (brand === "minibusgrab") return "MinibusGrab";
  if (brand === "cheapcoachgrab") return "CheapCoachGrab";
  if (brand === "planner") return "Airport Journey Planner";
  return "CheapCabGrab";
}

function initialState() {
  return {
    vehicle_preference: "Not sure",
    passengers: "",
    bags: "",
    pickup_datetime: "",
    pickup_location: "",
    dropoff_location: "",
    flight_number: "",
    flight_time: "",
    airport_code: "",
    terminal: "",
    direction: "",
    ajp_journey_id: "",
    booking_ref: "",
    name: "",
    email: "",
    phone: "",
    message: "",
  };
}

function digitCount(value) {
  return String(value || "").replace(/\D/g, "").length;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());
}

function isUsefulLocation(value) {
  const trimmed = String(value || "").trim();
  return trimmed.length >= 3 && /[a-z0-9]/i.test(trimmed);
}

const FloatingSupportChat = ({
  brand,
  defaultFlow = "",
  position = "right",
  showAfterMs = 12000,
  nudgeText = "Unsure? We can help.",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const resolvedBrand = useMemo(() => detectBrand(brand), [brand]);
  const resolvedBrandLabel = brandLabel(resolvedBrand);
  const [open, setOpen] = useState(false);
  const [nudged, setNudged] = useState(false);
  const [flow, setFlow] = useState(defaultFlow);
  const [variant, setVariant] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState(() => initialState());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const completedRef = useRef(false);
  const seenStepsRef = useRef(new Set());

  const steps = flow === "enquiry" && variant === "ajp_minibus"
    ? AJP_MINIBUS_ENQUIRY_STEPS
    : flow ? FLOW_STEPS[flow] || [] : [];
  const currentStep = steps[stepIndex];
  const atLastStep = flow && stepIndex >= steps.length - 1;

  useEffect(() => {
    if (!open || !flow || !currentStep || result) return;
    const key = `${flow}:${stepIndex}:${currentStep.key}`;
    if (seenStepsRef.current.has(key)) return;
    seenStepsRef.current.add(key);
    trackFunnelEvent("support_step_view", {
      brand: resolvedBrand,
      flow,
      step: currentStep.key,
      step_number: stepIndex + 1,
    });
  }, [currentStep, flow, open, resolvedBrand, result, stepIndex]);

  useEffect(() => {
    if (typeof window === "undefined" || !open || !flow || result) return undefined;
    const recordAbandon = () => {
      if (completedRef.current) return;
      trackFunnelExit("support_flow_abandon", {
        brand: resolvedBrand,
        flow,
        step: currentStep?.key,
        step_number: stepIndex + 1,
        status: "page_exit",
      });
    };
    window.addEventListener("pagehide", recordAbandon);
    return () => window.removeEventListener("pagehide", recordAbandon);
  }, [currentStep?.key, flow, open, resolvedBrand, result, stepIndex]);

  useEffect(() => {
    if (!showAfterMs) return undefined;
    const timer = window.setTimeout(() => setNudged(true), showAfterMs);
    return () => window.clearTimeout(timer);
  }, [showAfterMs]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (flow && !result) {
          completedRef.current = true;
          trackFunnelEvent("support_flow_abandon", {
            brand: resolvedBrand,
            flow,
            step: currentStep?.key,
            step_number: stepIndex + 1,
            status: "escape_key",
          });
        }
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [currentStep?.key, flow, open, resolvedBrand, result, stepIndex]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const openFromPage = (event) => {
      const nextFlow = event.detail?.flow || defaultFlow || "";
      const prefill = event.detail?.prefill;
      const source = event.detail?.source || "page_help_cta";
      const nextVariant = event.detail?.variant || "";
      trackFunnelEvent("support_chat_open", {
        brand: resolvedBrand,
        default_flow: nextFlow || "chooser",
        source,
      });
      if (nextFlow) {
        trackFunnelEvent("support_flow_start", {
          brand: resolvedBrand,
          flow: nextFlow,
          source,
        });
      }
      setFlow(nextFlow);
      setVariant(nextVariant);
      setStepIndex(0);
      if (prefill && typeof prefill === "object" && !Array.isArray(prefill)) {
        const allowedPrefill = Object.fromEntries(
          Object.entries(prefill).filter(([key, value]) =>
            Object.prototype.hasOwnProperty.call(initialState(), key) && value !== undefined && value !== null
          )
        );
        setForm({ ...initialState(), ...allowedPrefill });
      }
      setResult(null);
      setError("");
      completedRef.current = false;
      seenStepsRef.current.clear();
      setNudged(false);
      setOpen(true);
    };
    window.addEventListener("ccg:open-support-chat", openFromPage);
    return () => window.removeEventListener("ccg:open-support-chat", openFromPage);
  }, [defaultFlow, resolvedBrand]);

  const reset = () => {
    completedRef.current = false;
    seenStepsRef.current.clear();
    setFlow(defaultFlow || "");
    setVariant("");
    setStepIndex(0);
    setForm(initialState());
    setResult(null);
    setError("");
  };

  const selectFlow = (nextFlow) => {
    completedRef.current = false;
    seenStepsRef.current.clear();
    trackFunnelEvent("support_flow_start", { brand: resolvedBrand, flow: nextFlow });
    setFlow(nextFlow);
    setVariant("");
    setStepIndex(0);
    setResult(null);
    setError("");
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const validateCurrent = () => {
    if (!currentStep) return "";
    const value = String(form[currentStep.key] || "").trim();
    if (currentStep.optional && !value) return "";
    if (!value) return `${currentStep.label} is required.`;
    if (currentStep.key === "email" && !isValidEmail(value)) {
      return "Please enter a valid email address.";
    }
    if (currentStep.key === "phone") {
      const digits = digitCount(value);
      if (digits < 10 || digits > 15) return "Please enter a valid phone number.";
    }
    if (currentStep.key === "passengers" && Number(value) < 1) {
      return "Please enter at least 1 passenger.";
    }
    if (currentStep.key === "bags" && Number(value) < 0) {
      return "Please enter 0 or more bags.";
    }
    if ((currentStep.key === "pickup_location" || currentStep.key === "dropoff_location") && !isUsefulLocation(value)) {
      return "Please enter a postcode, airport, station or full address.";
    }
    if ((currentStep.key === "pickup_datetime" || currentStep.key === "flight_time") && new Date(value).getTime() < Date.now() - 60000) {
      return `Please choose a future ${currentStep.key === "flight_time" ? "flight" : "pickup"} date and time.`;
    }
    if (currentStep.key === "booking_ref" && value.length < 4) {
      return "Please enter a valid booking reference.";
    }
    return "";
  };

  const submitCase = async () => {
    setSubmitting(true);
    setError("");
    try {
      const source = typeof window !== "undefined" ? window.location : null;
      const payload = {
        ...form,
        flow_type: flow,
        brand: resolvedBrand,
        source_domain: source?.origin || "",
        source_path: source?.pathname || "",
        source_url: source?.href || "",
      };
      const res = await api.post("/api/support-cases", payload);
      completedRef.current = true;
      trackFunnelEvent("support_flow_complete", {
        brand: resolvedBrand,
        flow,
        step: currentStep?.key,
        step_number: stepIndex + 1,
      });
      if (flow === "enquiry") {
        setOpen(false);
        completeEnquiry(navigate, {
          refCode: res.data?.ref_code || "ENQ",
          brand: resolvedBrandLabel,
          pickup: form.pickup_location,
          dropoff: form.dropoff_location,
          passengers: form.passengers,
          bags: form.bags,
          pickupTime: form.pickup_datetime,
          vehicle: form.vehicle_preference || "Journey enquiry",
        });
        return;
      }
      setResult({
        refCode: res.data?.ref_code,
        message: res.data?.message || "Support case received.",
      });
    } catch (err) {
      setError(err.response?.data?.error || "Could not send this request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    const validationError = validateCurrent();
    if (validationError) {
      trackFunnelEvent("support_flow_validation_error", {
        brand: resolvedBrand, flow, step: currentStep?.key,
      });
      setError(validationError);
      return;
    }
    if (atLastStep) {
      trackFunnelEvent("support_flow_submit_click", {
        brand: resolvedBrand, flow, step: currentStep?.key,
      });
      submitCase();
      return;
    }
    trackFunnelEvent("support_flow_step_complete", {
      brand: resolvedBrand, flow, step: currentStep?.key, step_number: stepIndex + 1,
    });
    setStepIndex((idx) => idx + 1);
  };

  const back = () => {
    setError("");
    if (!flow) return;
    if (stepIndex === 0) {
      setFlow("");
      return;
    }
    setStepIndex((idx) => idx - 1);
  };

  const openSupportLink = (event, href) => {
    event.preventDefault();
    if (typeof window !== "undefined") {
      trackFunnelExit(href.startsWith("tel:") ? "call_click" : "whatsapp_click", {
        brand: resolvedBrand, source: "support_flow", step_name: currentStep?.key,
        quote_id: Number(window.location.pathname.match(/results\/(\d+)/)?.[1]) || undefined,
        conversion_role: "engagement",
      });
      window.location.href = href;
    }
  };

  const renderInput = () => {
    if (!currentStep) return null;
    const value = form[currentStep.key] || "";
    if (currentStep.type === "select") {
      return (
        <select value={value} onChange={(e) => updateField(currentStep.key, e.target.value)}>
          {currentStep.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    if (currentStep.type === "textarea") {
      return (
        <textarea
          value={value}
          rows={4}
          onChange={(e) => updateField(currentStep.key, e.target.value)}
          placeholder={currentStep.label}
        />
      );
    }
    return (
      <input
        type={currentStep.type}
        value={value}
        min={currentStep.type === "number" ? 0 : undefined}
        inputMode={currentStep.type === "tel" ? "tel" : currentStep.type === "number" ? "numeric" : undefined}
        onChange={(e) => updateField(currentStep.key, e.target.value)}
        placeholder={currentStep.label}
      />
    );
  };

  if (location.pathname === "/thank-you") return null;

  return (
    <div className={`floating-support floating-support-${position} ${open ? "floating-support-open" : ""}`}>
      {open && (
        <section className="floating-support-panel" aria-label={`${resolvedBrandLabel} support`}>
          <header>
            <div>
              <strong>{resolvedBrandLabel} support</strong>
              <span>Handled by CheapCabGrab</span>
            </div>
            <button type="button" onClick={() => {
              if (flow && !result) {
                completedRef.current = true;
                trackFunnelEvent("support_flow_abandon", {
                  brand: resolvedBrand, flow, step: currentStep?.key, step_number: stepIndex + 1,
                  status: "explicit_close",
                });
              }
              setOpen(false);
            }} aria-label="Close support">
              ×
            </button>
          </header>

          {result ? (
            <div className="floating-support-done">
              <h3>Request received</h3>
              <p>{result.message}</p>
              {result.refCode && <p className="floating-support-ref">Reference: {result.refCode}</p>}
              <button type="button" onClick={reset}>
                Start another request
              </button>
            </div>
          ) : !flow ? (
            <div className="floating-support-flows">
              <p>What do you need help with?</p>
              {FLOW_OPTIONS.map((option) => (
                <button type="button" key={option.id} onClick={() => selectFlow(option.id)}>
                  <strong>{option.label}</strong>
                  <span>{option.hint}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="floating-support-step">
              <div className="floating-support-progress">
                Step {stepIndex + 1} of {steps.length}
              </div>
              <label>
                <span>
                  {currentStep.label}
                  {currentStep.optional ? " (optional)" : ""}
                </span>
                {renderInput()}
              </label>
              {error && <div className="floating-support-error">{error}</div>}
              <div className="floating-support-actions">
                <button type="button" className="secondary" onClick={back} disabled={submitting}>
                  Back
                </button>
                <button type="button" onClick={next} disabled={submitting}>
                  {submitting ? "Sending..." : atLastStep ? "Send request" : "Next"}
                </button>
              </div>
            </div>
          )}

          <footer>
            <a href={SUPPORT_CALL_HREF} onClick={(event) => openSupportLink(event, SUPPORT_CALL_HREF)}>
              Call
            </a>
            <a href={SUPPORT_WHATSAPP_HREF} onClick={(event) => openSupportLink(event, SUPPORT_WHATSAPP_HREF)}>
              WhatsApp
            </a>
            <a href="/contact" target="_blank" rel="noreferrer">Contact page</a>
          </footer>
        </section>
      )}

      {!open && nudged && <div className="floating-support-nudge">{nudgeText}</div>}
      <button
        type="button"
        className={`floating-support-launcher ${nudged ? "is-nudged" : ""}`}
        onClick={() => {
          setNudged(false);
          if (!open) {
            completedRef.current = false;
            trackFunnelEvent("support_chat_open", {
              brand: resolvedBrand, default_flow: defaultFlow || "chooser",
            });
          }
          setOpen((value) => !value);
        }}
      >
        <span className="floating-support-label">Help?</span>
        <span className="floating-support-mobile-label">?</span>
      </button>
    </div>
  );
};

export default FloatingSupportChat;
